import React from 'react';
import { Box, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { GlassButton, ImageDropUploader } from 'dashboard/components/common';
import LinkListEditor from './LinkListEditor';

/**
 * Footer columns — the shape real footers have.
 *
 * A single row of fifteen links is what brands were working around: "Company",
 * "Legal", "Follow us" are separate lists with their own headings, and the
 * first column is usually a logo and a sentence rather than links at all.
 *
 * Four is the cap. Past that each column is too narrow to read on anything
 * but a wide desktop, and the extras land as a second row that looks like a
 * mistake.
 */
export default function FooterColumns({ columns = [], onChange, brandId }) {
  const set = (i, patch) => onChange(columns.map((c, j) => (i === j ? { ...c, ...patch } : c)));

  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= columns.length) return;
    const next = [...columns];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <Box>
      <Typography variant='caption' color='text.secondary' display='block' mb='10px'>
        Group your footer links under headings. Leave the columns empty to keep a single row.
      </Typography>

      <Stack spacing='12px'>
        {columns.map((col, i) => (
          <Box
            key={i}
            sx={{
              border: '1px solid', borderColor: 'divider',
              borderRadius: '12px', p: '14px',
            }}
          >
            <Stack direction='row' alignItems='center' justifyContent='space-between' mb='10px' gap='8px' flexWrap='wrap'>
              <Typography variant='caption' fontWeight='700' color='text.secondary'>
                Column {i + 1}
              </Typography>
              <Stack direction='row' spacing='2px' flexWrap='wrap'>
                <Tooltip title='Move left'>
                  <span>
                    <IconButton size='small' onClick={() => move(i, -1)} disabled={i === 0}>
                      <ArrowBackIcon fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title='Move right'>
                  <span>
                    <IconButton size='small' onClick={() => move(i, 1)} disabled={i === columns.length - 1}>
                      <ArrowForwardIcon fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title='Remove column'>
                  <IconButton
                    size='small'
                    color='error'
                    onClick={() => onChange(columns.filter((_, j) => j !== i))}
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Stack spacing='10px'>
              <TextField
                size='small'
                label='Heading'
                value={col.title || ''}
                onChange={(e) => set(i, { title: e.target.value })}
                placeholder='Company'
                fullWidth
              />
              <TextField
                size='small'
                label='Text'
                value={col.text || ''}
                onChange={(e) => set(i, { text: e.target.value })}
                placeholder='A sentence about the business.'
                multiline
                minRows={2}
                fullWidth
              />
              <Box>
                <Typography variant='caption' color='text.secondary' display='block' mb='6px'>
                  Image (optional) — usually only in the first column
                </Typography>
                <ImageDropUploader
                  brandId={brandId}
                  currentSrc={col.logoUrl || ''}
                  onUploaded={(url) => set(i, { logoUrl: url })}
                />
              </Box>
              <Box>
                <Typography variant='body2' fontWeight='600' mb='8px'>Links</Typography>
                <LinkListEditor
                  links={col.links || []}
                  onChange={(links) => set(i, { links })}
                  addLabel='Add link'
                />
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>

      <GlassButton
        size='small'
        startIcon={<AddIcon />}
        onClick={() => onChange([...columns, { title: '', text: '', logoUrl: '', links: [] }])}
        disabled={columns.length >= 4}
        disabledReason={columns.length >= 4
          ? 'Four columns is as many as a footer row can hold and stay readable.'
          : ''}
        sx={{ mt: '12px' }}
      >
        Add column
      </GlassButton>
    </Box>
  );
}
